import { DatasetType } from '@/types/automationFields.ts';

import { PaginatedResponse } from '@/types/common.ts';
import type { CreatorPrincipalType } from '@/types/automations.ts';

export type IntegrationProvider = {
  name: string;
  display_name: string;
  description: string;
  provider_kind?: ProviderKind;
  supports_linked_datasets: boolean;
  trigger_actions: IntegrationAction[];
  dispatch_actions: IntegrationAction[];
  instances: IntegrationInstance[];
  internal_integration: boolean;
  is_step_type: boolean;
};
export type IntegrationProviders = PaginatedResponse<IntegrationProvider>;

export enum ProviderKind {
  ExternalIntegration = 'external_integration',
  SystemIntegration = 'system_integration',
  StandaloneCapability = 'standalone_capability',
}
export type IntegrationInstance = {
  id: string;
  name: string;
  updated_at: string;
  integration_type: string;
  sharepoint_tenant_id?: string | null;
  created_by: string;
  created_by_type: CreatorPrincipalType;
  created_by_display_name: string;
  created_by_email: string;
  created_by_label: string;
};

export enum ConfigFieldType {
  TextInput = 'text_input',
  NumberInput = 'number_input',
  TextArea = 'text_area',
  Json = 'json',
  Select = 'select',
  DataInputSelect = 'data_input_select',
  DataInputMultiSelect = 'data_input_multi_select',
  Divider = 'divider',
  Webhook = 'webhook',
  Checkbox = 'checkbox',
  HelperText = 'helper_text',
  Email = 'email',
}

export type IntegrationInputContract = {
  accepted_dataset_types: string[];
  accepted_schema_refs: { schema_id: string; version: number }[];
  allowed_source_types: string[];
  required: boolean;
  allow_multiple: boolean;
  sensitive: boolean;
  purpose: string;
  validation_mode: 'error' | 'warn';
};

export type IntegrationConfigFieldBase<TType extends ConfigFieldType = ConfigFieldType> = {
  id: string;
  display_name: string;
  type: TType;
  required: boolean;
  default_value: unknown | null;
  description: string | null;
  placeholder: string | null;
  authoring_schema?: Record<string, unknown> | null;
  is_sensitive: boolean;
  input_contract?: IntegrationInputContract | null;
};
export type SelectOption = {
  label: string;
  value: string;
};
export type IntegrationSelectConfigField = IntegrationConfigFieldBase<ConfigFieldType.Select> & {
  static_options: boolean;
  options: SelectOption[];
};
type IntegrationTextFieldExtras = {
  max_length: number | null;
  right_section_text: string | null;
  read_only?: boolean;
};

export type IntegrationTextConfigField = IntegrationConfigFieldBase<
  | ConfigFieldType.TextInput
  | ConfigFieldType.TextArea
  | ConfigFieldType.Json
  | ConfigFieldType.Email
> &
  IntegrationTextFieldExtras;
export type IntegrationNumberConfigField = IntegrationConfigFieldBase<ConfigFieldType.NumberInput> &
  IntegrationTextFieldExtras;
export type IntegrationDataInputSelectConfigField =
  IntegrationConfigFieldBase<ConfigFieldType.DataInputSelect>;
export type IntegrationDataInputMultiSelectConfigField =
  IntegrationConfigFieldBase<ConfigFieldType.DataInputMultiSelect>;
export type IntegrationWebhookConfigField = IntegrationConfigFieldBase<ConfigFieldType.Webhook>;
export type IntegrationHelperTextConfigField =
  IntegrationConfigFieldBase<ConfigFieldType.HelperText>;
export type IntegrationCheckboxConfigField = IntegrationConfigFieldBase<ConfigFieldType.Checkbox>;
export type IntegrationDividerConfigField = IntegrationConfigFieldBase<ConfigFieldType.Divider>;
export type IntegrationConfigField =
  | IntegrationSelectConfigField
  | IntegrationTextConfigField
  | IntegrationNumberConfigField
  | IntegrationDataInputSelectConfigField
  | IntegrationDataInputMultiSelectConfigField
  | IntegrationWebhookConfigField
  | IntegrationHelperTextConfigField
  | IntegrationCheckboxConfigField
  | IntegrationDividerConfigField;
export type IntegrationActionConfig = {
  fields: IntegrationConfigField[];
};
export type TriggerActionInputPort = {
  binding_id: string;
  display_name: string;
  dataset_type: DatasetType;
  required: boolean;
  fixture_seedable: boolean;
};
export type IntegrationAction = {
  name: string;
  display_name: string;
  description: string;
  output_type: DatasetType | null;
  original_file_transport?: 'runtime_checked' | null;
  output_contract?: {
    dataset_type: DatasetType;
    schema_id?: string | null;
    version?: number | null;
    description: string;
    json_schema?: Record<string, unknown> | null;
    validation_mode?: 'error' | 'warn';
  } | null;
  artifact_output?: {
    dataset_type: DatasetType;
    profile: 'workbook' | 'blob_review' | 'external_resource';
    artifact_kind: 'immutable_file' | 'external_resource';
    version: number;
    expected_mime_types: string[];
    supports_snapshot: boolean;
    description: string;
  } | null;
  secondary_input_ports?: TriggerActionInputPort[];
  config: IntegrationActionConfig;
};

export enum EmailRouteKind {
  Generated = 'generated',
  Alias = 'alias',
}

export type EmailRoute = {
  id: string;
  local_part: string;
  email_address: string;
  route_kind: EmailRouteKind;
};

export type EmailRoutes = {
  generated_route: EmailRoute;
  aliases: EmailRoute[];
};

export type AutomationEmailIdentity = {
  email_address: string;
  display_name: string;
  display_name_override: string;
  reply_to_step_id: string;
};
